import Swal from 'sweetalert2';
import { getAxiosClient } from '~src/api';
import { onErrorShowMessage } from '~src/errors';
import { fetchTemplateById } from '~src/templates';
import { isLoggedUserGlobalEditor } from '~src/user';
import {
  enableLoaderInput,
  disableLoaderInput,
  loadingBox,
} from '~loader/loader-utils';
import {
  attachDuplicatePageToTemplate,
  buildDuplicateTemplateData,
  extractTemplatePages,
  getPointerId,
  prepareDuplicatePageData,
} from './duplicateTemplatePayload';

const getTargetConfig = (isGlobalTarget) => {
  if (isGlobalTarget) {
    return {
      templateTable: 'editor_templates_default',
      pageTable: 'editor_templates_default_pages',
    };
  }

  return {
    templateTable: 'editor_templates',
    pageTable: 'editor_templates_pages',
  };
};

const findTemplateById = async (templateId) => {
  const templates = window.editorConfig?.sidebarTemplates;

  const templateToDuplicate = templates?.items?.find(
    (template) => template.objectId === templateId
  );

  if (templateToDuplicate) return templateToDuplicate;

  return fetchTemplateById(templateId, { updateSelection: false });
};

const saveDuplicatedTemplate = async (httpClient, tableName, templateData) => {
  const response = await httpClient.post(
    `${process.env.PARSE_API_URL}/${tableName}`,
    {
      data: templateData,
    }
  );
  const template = response?.data?.data;

  if (!getPointerId(template)) {
    throw new Error('Duplicated template id not found');
  }

  return template;
};

const saveDuplicateTemplatePages = async (httpClient, tableName, pagesData) => {
  const responses = await Promise.all(
    pagesData.map((data) =>
      httpClient.post(`${process.env.PARSE_API_URL}/${tableName}`, {
        data,
      })
    )
  );

  return responses.map((response) => {
    const pageId = getPointerId(response?.data?.data);

    if (!pageId) {
      throw new Error('Duplicated template page id not found');
    }

    return pageId;
  });
};

const saveDuplicatedTemplateAndPagesRelation = async (
  httpClient,
  tableName,
  templateId,
  pageIds
) => {
  const url = `${process.env.PARSE_API_URL}/${tableName}/${templateId}`;

  return httpClient.put(url, {
    data: {
      relations: [
        {
          class: `${tableName}_pages`,
          column: `${tableName}_pages`,
          toIds: pageIds,
        },
      ],
    },
  });
};

export const duplicateTemplate = async (templateId) => {
  disableLoaderInput();

  try {
    loadingBox('Duplico il modello scelto...');

    const isGlobalTarget = await isLoggedUserGlobalEditor();
    const templateToDuplicate = await findTemplateById(templateId);

    if (!getPointerId(templateToDuplicate)) {
      throw new Error('Template not found');
    }

    const { templateTable, pageTable } = getTargetConfig(isGlobalTarget);
    const templateData = buildDuplicateTemplateData(
      templateToDuplicate,
      isGlobalTarget
    );
    const pagesData = extractTemplatePages(templateToDuplicate).map((page) =>
      prepareDuplicatePageData(page, isGlobalTarget)
    );
    const httpClient = await getAxiosClient();
    const template = await saveDuplicatedTemplate(
      httpClient,
      templateTable,
      templateData
    );
    const duplicatedPagesData = pagesData.map((page) =>
      attachDuplicatePageToTemplate(page, template, isGlobalTarget)
    );
    const duplicatedPageIds = await saveDuplicateTemplatePages(
      httpClient,
      pageTable,
      duplicatedPagesData
    );

    await saveDuplicatedTemplateAndPagesRelation(
      httpClient,
      templateTable,
      template.objectId,
      duplicatedPageIds
    );

    if (window.editorLoader) {
      window.editorLoader.templates = {};
    }

    Swal.fire({
      title: 'Duplicato!',
      html:
        'Il nuovo modello &egrave; stato duplicato ed è ora pronto per essere utilizzato.',
      icon: 'success',
      confirmButtonColor: '#278e8e',
    });

    return template;
  } catch (error) {
    console.error(error);
    onErrorShowMessage();
    return null;
  } finally {
    enableLoaderInput();
  }
};
